import { iTrack } from '../types/ITrack';
import {
  StreamingExportPhase,
  StreamingExportProgress,
  StreamingExportTrack,
  StreamingServiceId,
} from '../types/Streaming';
import AppleMusicService from './AppleMusic.service';
import SpotifyService from './Spotify.service';
import { findStreamingMatch } from './StreamingMatch.service';
import TidalService from './Tidal.service';

/**
 * Building a brand new playlist on a streaming service from a list of tracks.
 * The crate save buttons own the sync path (existing playlist, missing tracks);
 * this is the one-shot create used by the agent.
 */

export interface StreamingExportResult {
  playlistId: string | null;
  matched: StreamingExportTrack[];
  unmatched: StreamingExportTrack[];
}

export interface StreamingExportOptions {
  service: StreamingServiceId;
  playlistName: string;
  description?: string;
  tracks: iTrack[];
}

const DEFAULT_DESCRIPTION = 'Created by Crate Hackers';

const label = (track: iTrack): StreamingExportTrack => ({
  artist: track.Artist || '',
  title: track.Title || '',
});

const createPlaylist = async (
  service: StreamingServiceId,
  name: string,
  description: string,
): Promise<string | null> => {
  if (service === 'spotify') {
    const playlist = await SpotifyService.createPlaylist(name, description);
    return playlist?.id ?? null;
  }
  if (service === 'apple') {
    return (await AppleMusicService.createPlaylist(name, description)) ?? null;
  }
  const response = await TidalService.createPlaylist(name, description);
  return response?.data?.id ?? null;
};

const addTracks = async (
  service: StreamingServiceId,
  playlistId: string,
  trackIds: string[],
): Promise<void> => {
  if (service === 'spotify') {
    await SpotifyService.addTracksToPlaylist(
      playlistId,
      trackIds.map((id) => `spotify:track:${id}`),
    );
    return;
  }
  if (service === 'apple') {
    await AppleMusicService.addTracksToPlaylist(playlistId, trackIds);
    return;
  }
  await TidalService.addTracksToPlaylist(playlistId, trackIds);
};

/**
 * Searching once per track is what trips streaming rate limits, so long lists
 * get a wider gap between lookups.
 */
const matchDelay = (total: number): number => (total > 50 ? 200 : 100);

export const exportTracksToStreaming = async (
  options: StreamingExportOptions,
  onProgress?: (progress: StreamingExportProgress) => void,
): Promise<StreamingExportResult> => {
  const { service, playlistName, tracks } = options;
  const total = tracks.length;

  const matched: StreamingExportTrack[] = [];
  const unmatched: StreamingExportTrack[] = [];
  const matchedIds: string[] = [];

  const report = (
    phase: StreamingExportPhase,
    message: string,
    currentTrack = '',
  ) =>
    onProgress?.({
      service,
      playlistName,
      phase,
      matched: matched.length,
      total,
      currentTrack,
      message,
    });

  try {
    for (let i = 0; i < tracks.length; i += 1) {
      const track = tracks[i];
      report(
        'matching',
        `Matching ${i + 1} of ${total}`,
        `${track.Artist} - ${track.Title}`,
      );

      const { matched: found } = await findStreamingMatch(track, service);
      if (found?.ID) {
        matchedIds.push(found.ID);
        matched.push(label(track));
      } else {
        unmatched.push(label(track));
      }

      if (i < tracks.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, matchDelay(total)));
      }
    }

    report('creating', `Creating "${playlistName}"`);
    const playlistId = await createPlaylist(
      service,
      playlistName,
      options.description || DEFAULT_DESCRIPTION,
    );

    if (!playlistId) {
      throw new Error('The service did not return a playlist id.');
    }

    if (matchedIds.length > 0) {
      report('adding', `Adding ${matchedIds.length} tracks`);
      await addTracks(service, playlistId, matchedIds);
    }

    report('done', `Added ${matched.length} of ${total} tracks`);
    return { playlistId, matched, unmatched };
  } catch (error) {
    report(
      'error',
      error instanceof Error ? error.message : 'The export failed.',
    );
    throw error;
  }
};
